/**
 * @file TabContextConflictDialog.tsx
 * @description Окно «не тот кабинет у вкладки» (phase19.31, SC22, SC42).
 *
 * Сервер отказывает, если кабинет вкладки (заголовок `X-Commora-Context`)
 * больше не принадлежит сотруднику (403 `rbac_tab_context_forbidden`) или
 * если в другой вкладке идёт вход под сотрудником (409
 * `impersonation_active`). `api-client` поднимает событие
 * `TAB_CONTEXT_CONFLICT_EVENT`, а это окно объясняет, что произошло, и даёт
 * действие — вместо сырой ошибки на каждой странице.
 *
 * Действия перезагружают страницу с главного адреса: память вкладки (её
 * кабинет) очищается, и кабинет выбирается заново. Профиль (`/auth/me`)
 * сам справляется с отказом и окна не вызывает.
 */

import { useEffect, useState } from "react";

import { ApiError, TAB_CONTEXT_CONFLICT_EVENT, TAB_CONTEXT_CONFLICT_TEXT } from "../lib/api-client";
import { stopImpersonation } from "../lib/api-impersonation";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { useNotify } from "./ui/NotifyProvider";

type ConflictCode = "rbac_tab_context_forbidden" | "impersonation_active";

/** Заголовки окна по коду отказа — реестр. Тексты — общий реестр api-client. */
const TITLE: Record<ConflictCode, string> = {
  rbac_tab_context_forbidden: "Нет доступа к кабинету этой вкладки",
  impersonation_active: "Идёт вход под сотрудником",
};

function isConflictCode(code: unknown): code is ConflictCode {
  return code === "rbac_tab_context_forbidden" || code === "impersonation_active";
}

/** Перезагрузка с главной: кабинет вкладки выбирается заново. */
function reloadHome(): void {
  window.location.assign(import.meta.env.BASE_URL);
}

export function TabContextConflictDialog() {
  const notify = useNotify();
  const [code, setCode] = useState<ConflictCode | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    function onConflict(event: Event) {
      const detail = (event as CustomEvent<{ code?: unknown; path?: unknown }>).detail;
      if (detail?.path === "/auth/me" || !isConflictCode(detail?.code)) return;
      setCode(detail.code);
    }
    window.addEventListener(TAB_CONTEXT_CONFLICT_EVENT, onConflict);
    return () => window.removeEventListener(TAB_CONTEXT_CONFLICT_EVENT, onConflict);
  }, []);

  async function onStopImpersonation() {
    setBusy(true);
    try {
      await stopImpersonation();
      reloadHome();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось завершить вход под сотрудником");
      setBusy(false);
    }
  }

  return (
    <AlertDialog open={code !== null} onOpenChange={(open) => !open && setCode(null)}>
      {code ? (
        <AlertDialogContent fullScreen>
          <AlertDialogHeader>
            <AlertDialogTitle>{TITLE[code]}</AlertDialogTitle>
            <AlertDialogDescription>{TAB_CONTEXT_CONFLICT_TEXT[code]}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="ghost" onClick={() => setCode(null)} disabled={busy}>
              Закрыть
            </Button>
            {code === "impersonation_active" ? (
              <>
                <Button variant="outline" onClick={reloadHome} disabled={busy}>
                  Перейти в его кабинет
                </Button>
                <Button variant="warning" onClick={() => void onStopImpersonation()} disabled={busy}>
                  {busy ? "Завершаем…" : "Завершить вход"}
                </Button>
              </>
            ) : (
              <Button onClick={reloadHome}>Открыть свой кабинет</Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      ) : null}
    </AlertDialog>
  );
}
